import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const IT_CODES = [
  { code: '541511', title: 'Custom Computer Programming Services', primary: true },
  { code: '541512', title: 'Computer Systems Design Services', primary: true },
  { code: '541513', title: 'Computer Facilities Management Services', primary: false },
  { code: '518210', title: 'Data Processing, Hosting, and Related Services', primary: false },
];

const CONSULTING_CODES = [
  { code: '541611', title: 'Administrative Management & General Management Consulting Services', primary: true },
  { code: '541618', title: 'Other Management Consulting Services', primary: false },
  { code: '541219', title: 'Other Accounting Services', primary: false },
  { code: '541690', title: 'Other Scientific and Technical Consulting Services', primary: false },
  { code: '541990', title: 'All Other Professional, Scientific, and Technical Services', primary: false },
];

const FACILITIES_CODES = [
  { code: '561210', title: 'Facilities Support Services', primary: true },
  { code: '541410', title: 'Interior Design Services', primary: false },
  { code: '541330', title: 'Engineering Services', primary: false },
  { code: '561621', title: 'Security Systems Services', primary: false },
];

const CodeRow = ({ code, title, primary }) => (
  <tr className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
    <td className="py-4 px-6">
      <span className="font-mono font-bold text-navy-800 text-sm">{code}</span>
      {primary && (
        <span className="ml-2 text-xs bg-gold-500/10 border border-gold-300 text-gold-700 px-2 py-0.5 rounded-full font-semibold">
          Primary
        </span>
      )}
    </td>
    <td className="py-4 px-6 text-slate-600 text-sm">{title}</td>
  </tr>
);

const NAICSCodes = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">NAICS Codes</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">About DTX</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading mb-4">NAICS Codes</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            DTX Digital Transformation operates under the following North American Industry Classification System
            codes, as registered in SAM.gov.
          </p>
        </div>
      </section>

      {/* NAICS tables */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 bg-navy-800 flex items-center gap-3">
              <span className="text-xl">💻</span>
              <h2 className="text-white font-bold font-heading">Information Technology Services</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200">
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-48">
                    NAICS Code
                  </th>
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {IT_CODES.map((c) => <CodeRow key={c.code} {...c} />)}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 bg-navy-800 flex items-center gap-3">
              <span className="text-xl">📊</span>
              <h2 className="text-white font-bold font-heading">Management Consulting, Risk &amp; Compliance</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200">
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-48">
                    NAICS Code
                  </th>
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONSULTING_CODES.map((c) => <CodeRow key={c.code} {...c} />)}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 bg-navy-800 flex items-center gap-3">
              <span className="text-xl">🏢</span>
              <h2 className="text-white font-bold font-heading">Facilities Management &amp; Space Planning</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200">
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider w-48">
                    NAICS Code
                  </th>
                  <th className="py-3 px-6 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {FACILITIES_CODES.map((c) => <CodeRow key={c.code} {...c} />)}
              </tbody>
            </table>
          </div>

          <div className="bg-gold-500/10 border border-gold-200 rounded-2xl p-6 text-sm text-slate-600">
            <p className="font-semibold text-navy-800 mb-1">Note to Contracting Officers</p>
            <p>
              These NAICS codes reflect DTX's registered capabilities in SAM.gov. Please verify against current
              SAM.gov registration or contact us directly to confirm applicable codes for your specific solicitation.
            </p>
          </div>

          <div className="text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-navy-800 hover:bg-navy-700 text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors"
            >
              Contact Us for NAICS Questions
            </Link>
          </div>

        </div>
      </section>

    </div>
  );
};

export default NAICSCodes;
